import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Card, CardContent, CardHeader, Grid, Typography, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  CircularProgress, Alert,
} from '@mui/material';
import { CheckCircle, Warning } from '@mui/icons-material';
import { fetchLedgerRevenue } from '../../../store/slices/ledgerSlice';

const fmt = (p) => p != null
  ? `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  : '—';

const DeltaChip = ({ billing, ledger }) => {
  if (billing == null || ledger == null) return null;
  const delta = Math.abs(billing - ledger);
  if (delta === 0) return <Chip icon={<CheckCircle />} label="Match" color="success" size="small" />;
  return (
    <Chip icon={<Warning />} label={`Δ ${fmt(delta)}`} color="error" size="small" />
  );
};

const LedgerRevenueComparison = () => {
  const dispatch = useDispatch();
  const { revenue, loading, error } = useSelector(s => s.ledger);

  useEffect(() => { dispatch(fetchLedgerRevenue()); }, [dispatch]);

  if (loading.revenue) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }
  if (error.revenue) {
    return <Alert severity="error">{error.revenue}</Alert>;
  }
  if (!revenue) return null;

  const summaryRows = [
    { label: 'Total GMV (Billing)', billing: revenue.billing_gmv_paise, ledger: revenue.ledger_gmv_paise },
    { label: 'Platform Revenue', billing: revenue.billing_platform_paise, ledger: revenue.ledger_platform_paise },
    { label: 'Astrologer Credits', billing: revenue.billing_astrologer_paise, ledger: revenue.ledger_astrologer_paise },
  ];

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {summaryRows.map(row => (
          <Grid item xs={12} md={4} key={row.label}>
            <Card elevation={2}>
              <CardHeader
                title={row.label}
                titleTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }}
                action={<DeltaChip billing={row.billing} ledger={row.ledger} />}
                sx={{ pb: 0 }}
              />
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Billing Events</Typography>
                    <Typography variant="h6" fontWeight="bold">{fmt(row.billing)}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">Ledger Derived</Typography>
                    <Typography variant="h6" fontWeight="bold">{fmt(row.ledger)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {revenue.by_service && revenue.by_service.length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>By Service Type</Typography>
          <TableContainer component={Paper} elevation={1}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell align="right">GMV (Billing)</TableCell>
                  <TableCell align="right">GMV (Ledger)</TableCell>
                  <TableCell align="right">Delta</TableCell>
                  <TableCell align="right">Sessions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {revenue.by_service.map((row) => {
                  const delta = (row.billing_paise ?? 0) - (row.ledger_paise ?? 0);
                  return (
                    <TableRow key={row.service_type} hover>
                      <TableCell>{row.service_type}</TableCell>
                      <TableCell align="right">{fmt(row.billing_paise)}</TableCell>
                      <TableCell align="right">{fmt(row.ledger_paise)}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{ color: delta === 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}
                        >
                          {delta === 0 ? '✓' : fmt(Math.abs(delta))}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{row.session_count ?? '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default LedgerRevenueComparison;
